import { gql, type TypedDocumentNode } from '@apollo/client';
import { type CSSProperties, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconAlertCircle } from '@sorare/core/src/atoms/icons/system/iconAlertCircle.ts';
import { iconHourglass } from '@sorare/core/src/atoms/icons/system/iconHourglass.ts';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { JonumKycPromptDialog } from '@sorare/core/src/components/jonum/JonumVerificationDialog/JonumKycPromptDialog';
import {
  type KycClaimState,
  getKycClaimState,
  useJonumVerificationGuard,
} from '@sorare/core/src/components/jonum/JonumVerificationDialog/useJonumVerificationGuard';
import { glossary } from '@sorare/core/src/lib/glossary';
import { css } from '@sorare/css';

import type { KycVerificationBanner_thresholdPickerTask } from './__generated__/index.graphql.ts';

const getTaskKycClaimState = (
  task: KycVerificationBanner_thresholdPickerTask,
  kycFlags: { kycRequired: boolean; mangopayKycUnderReview: boolean }
): KycClaimState | undefined => {
  if (task.aasmState !== ManagerProgressionTaskState.COMPLETED) {
    return undefined;
  }

  return getKycClaimState(kycFlags, task.rewardConfigs);
};

const banner = css`
  display: flex;
  width: 100%;
  max-width: 480px;
  align-items: center;
  gap: var(--unit);
  border: 1px solid var(--banner-border-color, var(--c-white));
  border-radius: var(--radius-m);
  background: var(--c-nl-500);
  padding: var(--intermediate-unit) var(--double-unit);
  color: var(--c-white);
  backdrop-filter: blur(32px);
`;

type Props = {
  tasks: readonly KycVerificationBanner_thresholdPickerTask[];
};

export const KycVerificationBanner = ({ tasks }: Props) => {
  const { formatMessage } = useIntl();
  const { kycRequired, mangopayKycUnderReview } = useJonumVerificationGuard();
  const [dismissed, setDismissed] = useState(false);
  const [openKycPromptDialog, setOpenKycPromptDialog] = useState(false);

  const kycState = tasks
    .map(task =>
      getTaskKycClaimState(task, { kycRequired, mangopayKycUnderReview })
    )
    .find(state => state !== undefined);

  const actionRequired = kycState === 'actionRequired';
  const pendingVerification = kycState === 'pendingVerification';

  if ((!actionRequired && !pendingVerification) || dismissed) {
    return null;
  }

  return (
    <>
      <LinkBox
        className={banner}
        style={
          {
            '--banner-border-color': actionRequired
              ? 'var(--c-yellow-400)'
              : 'var(--c-white)',
          } as CSSProperties
        }
      >
        <FontAwesomeIcon
          icon={actionRequired ? iconAlertCircle : iconHourglass}
          color={actionRequired ? 'var(--c-yellow-400)' : undefined}
          fontSize={12}
        />
        {actionRequired ? (
          <LinkOverlay
            as="button"
            type="button"
            className="min-w-0 flex-1 text-left"
            onClick={() => setOpenKycPromptDialog(true)}
          >
            <LabelS as="span">
              <FormattedMessage defaultMessage="You need to verify your account in order to claim your reward and create new lineups." />
            </LabelS>
          </LinkOverlay>
        ) : (
          <LabelS as="span" className="min-w-0 flex-1">
            <FormattedMessage defaultMessage="Your account is pending verification, you will be able to claim your reward once it's done." />
          </LabelS>
        )}
        <LinkOther
          as="button"
          type="button"
          className="inline-flex items-center justify-center"
          onClick={() => setDismissed(true)}
          aria-label={formatMessage(glossary.close)}
        >
          <FontAwesomeIcon icon={iconX} fontSize={16} />
        </LinkOther>
      </LinkBox>
      {!!actionRequired && (
        <JonumKycPromptDialog
          open={openKycPromptDialog}
          onDone={() => setOpenKycPromptDialog(false)}
        />
      )}
    </>
  );
};

KycVerificationBanner.fragments = {
  thresholdPickerTask: gql`
    fragment KycVerificationBanner_thresholdPickerTask on ThresholdPickerTask {
      id
      aasmState
      rewardConfigs {
        id
      }
    }
  ` as TypedDocumentNode<KycVerificationBanner_thresholdPickerTask>,
};
